import { Trans, useTranslation } from "react-i18next"

import { ButtonLink } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { LoaderCircle } from "lucide-react"

import { isFilled } from "lopermut"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useCampaignSaveContext } from "modules/campaigns/hooks/useCampaignSave"
import { ComposerPromptValidationIssues } from "modules/shared/components/ComposerPromptValidationIssues"
import { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { z } from "zod"

type FocusedField = "description" | "toneOfVoice" | null

export function BriefSubTab() {
  const { t } = useTranslation()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const { issuesByField, savingFields, setValidationReviewOpen, setComposerPromptEditing } =
    useCampaignSaveContext()
  const description = form.watch("description") ?? ""
  const toneOfVoice = form.watch("toneOfVoice") ?? ""
  const [focusedField, setFocusedField] = useState<FocusedField>(null)
  const [toneOfVoiceEnabled, setToneOfVoiceEnabled] = useState(isFilled(toneOfVoice))

  useEffect(() => () => setComposerPromptEditing(false), [setComposerPromptEditing])

  function handleToneOfVoiceToggle(checked: boolean) {
    setToneOfVoiceEnabled(checked)
    if (checked) {
      setFocusedField("toneOfVoice")
    } else {
      form.setValue("toneOfVoice", null, { shouldDirty: true })
      setFocusedField(null)
    }
  }

  function applySuggestion(field: string, value: string) {
    if (field !== "description" && field !== "toneOfVoice") return
    form.setValue(field, value, { shouldDirty: true })
  }

  return (
    <div className="flex flex-col gap-4">
      <Card
        className={cn(
          "flex flex-col",
          focusedField === "description" && "border-ring ring-ring/50 ring-[3px]",
          issuesByField.description?.length && "border-destructive/50 ring-1 ring-destructive/15",
        )}
      >
        <div className="flex flex-col gap-0 p-4 bg-muted rounded-t-md">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">
              {t("campaigns.detail.customization.brief.title")}
            </h3>
            <div className="flex h-9 w-9 items-center justify-center">
              {savingFields.includes("description") ? (
                <LoaderCircle className="h-5 w-5 animate-spin" />
              ) : (
                <ComposerPromptValidationIssues
                  issues={issuesByField.description ?? []}
                  getFieldText={(field) => (field === "description" ? description : toneOfVoice)}
                  onApplySuggestion={applySuggestion}
                  onOpenChange={setValidationReviewOpen}
                />
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("campaigns.detail.customization.brief.description")}
          </p>
        </div>
        <Textarea
          value={description}
          onChange={(e) => form.setValue("description", e.target.value, { shouldDirty: true })}
          onFocus={() => {
            setFocusedField("description")
            setComposerPromptEditing(true)
          }}
          onBlur={() => {
            setFocusedField(null)
            setComposerPromptEditing(false, "description")
          }}
          placeholder={t("campaigns.detail.customization.brief.placeholder")}
          rows={8}
          className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none bg-transparent dark:bg-transparent ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
          maxLength={2048}
          maxLengthClassName="p-4 text-muted-foreground/70"
        />
      </Card>
      <Card
        className={cn(
          "flex flex-col",
          focusedField === "toneOfVoice" && "border-ring ring-ring/50 ring-[3px]",
          issuesByField.toneOfVoice?.length && "border-destructive/50 ring-1 ring-destructive/15",
        )}
      >
        <div className="relative flex flex-col gap-2 rounded-t-md bg-muted p-4">
          <div className="flex flex-col gap-0">
            <h3 className="text-lg font-medium">
              {t("campaigns.detail.customization.toneOfVoice.title")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("campaigns.detail.customization.toneOfVoice.description")}
            </p>
          </div>
          <div className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center">
            {savingFields.includes("toneOfVoice") ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : (
              <ComposerPromptValidationIssues
                issues={issuesByField.toneOfVoice ?? []}
                getFieldText={(field) => (field === "description" ? description : toneOfVoice)}
                onApplySuggestion={applySuggestion}
                onOpenChange={setValidationReviewOpen}
              />
            )}
          </div>
          <div className="flex items-center gap-3 rounded-lg border bg-background p-3 max-w-4xl w-fit">
            <Switch
              id="tone-of-voice-switch"
              checked={toneOfVoiceEnabled}
              onCheckedChange={handleToneOfVoiceToggle}
            />
            <Label
              htmlFor="tone-of-voice-switch"
              className="text-sm font-medium cursor-pointer text-foreground flex-1"
            >
              {t("campaigns.detail.customization.toneOfVoice.useCustom")}
            </Label>
          </div>
        </div>
        {toneOfVoiceEnabled ? (
          <Textarea
            value={toneOfVoice}
            onChange={(e) => form.setValue("toneOfVoice", e.target.value, { shouldDirty: true })}
            onFocus={() => {
              setFocusedField("toneOfVoice")
              setComposerPromptEditing(true)
            }}
            onBlur={() => {
              setFocusedField(null)
              setComposerPromptEditing(false, "toneOfVoice")
            }}
            placeholder={t("campaigns.detail.customization.toneOfVoice.placeholder")}
            rows={8}
            className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none bg-transparent dark:bg-transparent ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
            maxLength={2048}
            maxLengthClassName="p-4 text-muted-foreground/70"
          />
        ) : (
          <span className="p-4 text-base text-muted-foreground">
            <Trans
              i18nKey="campaigns.detail.customization.toneOfVoice.defaultUsage"
              components={{
                a: (
                  <ButtonLink
                    to="/settings/customization"
                    search={{ tab: "agent" }}
                    className="p-0 h-auto"
                    variant="link"
                  />
                ),
              }}
            />
          </span>
        )}
      </Card>
    </div>
  )
}
